import {
  WEEKDAY_LABELS,
  WEEK_LABELS,
  WEEKDAY_CHART_PRESETS,
  WEEKLY_CHART_PRESETS,
  SINGLE_DAY_PRESETS
} from "./widgetConstants";

const SINGLE_DAY_LABELS = { today: "Today", yesterday: "Yesterday" };

/**
 * Groups raw rate series data into chart-ready buckets for the given preset.
 * TODAY/YESTERDAY have exactly one day in range, so they get a single-point
 * bucket (one x-axis category) instead of being grouped into weekday/weekly buckets.
 * If the API already returns pre-grouped chartData, pass it through directly.
 */
export const buildChartData = (series = [], preset) => {
  if (SINGLE_DAY_PRESETS.has(preset)) {
    return buildSingleDayBucket(series, preset);
  }

  if (WEEKDAY_CHART_PRESETS.has(preset)) {
    return buildWeekdayBuckets(series);
  }

  if (WEEKLY_CHART_PRESETS.has(preset)) {
    return buildWeeklyBuckets(series);
  }

  return [];
};

const pointValue = point => point.count != null ? point.count : (point.rate || 0);

const buildSingleDayBucket = (series, preset) => {
  const label = SINGLE_DAY_LABELS[preset] || "Today";
  const sum = series.reduce((total, point) => total + pointValue(point), 0);
  return [{ label, value: parseFloat(sum.toFixed(2)) }];
};

const buildWeekdayBuckets = series => {
  const buckets = WEEKDAY_LABELS.map(label => ({ label, sum: 0, count: 0 }));

  series.forEach(point => {
    const date = new Date(point.date || point.activityDate);
    const jsDay = date.getDay();
    const idx = jsDay === 0 ? 6 : jsDay - 1;
    buckets[idx].sum += pointValue(point);
    buckets[idx].count += 1;
  });

  return buckets.map(({ label, sum, count }) => ({
    label,
    value: count > 0 ? parseFloat((sum / count).toFixed(2)) : 0
  }));
};

const buildWeeklyBuckets = series => {
  const buckets = WEEK_LABELS.map(label => ({ label, sum: 0, count: 0 }));

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  series.forEach(point => {
    const date = new Date(point.date || point.activityDate);
    // Skip dates outside the current month
    if (date.getMonth() !== currentMonth || date.getFullYear() !== currentYear) return;
    const dayOfMonth = date.getDate();
    const weekIdx = Math.min(Math.floor((dayOfMonth - 1) / 7), 3);
    buckets[weekIdx].sum += pointValue(point);
    buckets[weekIdx].count += 1;
  });

  return buckets.map(({ label, sum, count }) => ({
    label,
    value: count > 0 ? parseFloat((sum / count).toFixed(2)) : 0
  }));
};

/**
 * If the API returns pre-grouped chartData array, use it directly.
 * Falls back to buildChartData for raw series.
 */
export const resolveChartData = (apiResponse, preset) => {
  if (apiResponse?.chartData?.length) {
    return apiResponse.chartData;
  }
  return buildChartData(apiResponse?.series || [], preset);
};
